import { get, isEmpty, isNil, isNumber } from 'lodash'
import * as Types from '../../types'
import surveyQuestionBank from '../../../communityConfigs/survey-questions.json'
import { SURVEY_ATTEMPT_STATES } from 'config'
import {
  hasQuestionWithDisplayComponent,
  isQuestionAnswered,
  isQuestionApplicable,
} from '../survey-question-status'

export const SECTION_KEYS = {
  STAFFING_INSTRUCTION: 'staffing_and_instruction',
  COMMUNITY_PARTNERSHIPS: 'community_and_partnerships',
  BUDGET_PLANNING: 'budget_and_planning',
  SUBMIT: 'submit',
}

export const SECTION_META = {
  [SECTION_KEYS.STAFFING_INSTRUCTION]: {
    label: 'Staffing & Instruction',
  },
  [SECTION_KEYS.COMMUNITY_PARTNERSHIPS]: {
    label: 'Community & Partnerships',
  },
  [SECTION_KEYS.BUDGET_PLANNING]: {
    label: 'Budget & Planning',
  },
  [SECTION_KEYS.SUBMIT]: {
    label: 'Submit',
  },
}

const { REQUIRED } = Types.SURVEY_QUESTIONS_NECESSITIES

const { SPACE_TYPES_FORM } = Types.QUESTION_DISPLAY_COMPONENTS

const {
  APPROACHES,
  INTEGRATIONS,
  GOVERNANCE,
  DISTRICT_SPENDING,
  COMMUNITY_ENGAGEMENTS,
} = Types.SURVEY_QUESTIONS

export function getSections() {
  return [
    SECTION_KEYS.STAFFING_INSTRUCTION,
    SECTION_KEYS.COMMUNITY_PARTNERSHIPS,
    SECTION_KEYS.BUDGET_PLANNING,
    SECTION_KEYS.SUBMIT,
  ]
}

export function computePercent(completedCount, totalCount) {
  if (!totalCount) return 0
  if (completedCount <= 0) return 0
  return Math.round((completedCount / totalCount) * 100)
}

function validatePercentAccess(school, participation) {
  if (school.category === Types.HIGH_SCHOOL_TYPE) return true
  return isNumber(get(participation, 'courseEnrollment'))
}

function getRequiredQuestionsForPage(surveyQuestions, surveyPage) {
  return (surveyQuestions || []).filter(
    (question) =>
      question.surveyPage === surveyPage && question.necessity === REQUIRED
  )
}

function getCommunityQuestionOption(communitySurveyQuestions, questionName) {
  const optionName = get(communitySurveyQuestions, questionName)
  if (!optionName) return null

  return get(surveyQuestionBank, `${questionName}.${optionName}`)
}

function hasRequiredConfiguredResponse(
  school,
  communitySurveyQuestions,
  questionName
) {
  const option = getCommunityQuestionOption(
    communitySurveyQuestions,
    questionName
  )
  if (!option || !option.error) return true

  return !isEmpty(get(school, option.responseType))
}

function hasRequiredQuestionResponse(question, school, disciplines) {
  if (!isQuestionApplicable(question, school)) return true

  return isQuestionAnswered(question, school, { disciplines })
}

function isStaffingInstructionComplete(data) {
  const {
    school,
    participation,
    surveyQuestions,
    communitySurveyQuestions,
    disciplines,
  } = data
  const requiredQuestions = getRequiredQuestionsForPage(
    surveyQuestions,
    Types.SURVEY_PAGES.STAFFING_AND_INSTRUCTION
  )

  return (
    validatePercentAccess(school, participation) &&
    hasRequiredConfiguredResponse(
      school,
      communitySurveyQuestions,
      APPROACHES.NAME
    ) &&
    hasRequiredConfiguredResponse(
      school,
      communitySurveyQuestions,
      INTEGRATIONS.NAME
    ) &&
    !isNil(get(school, 'surveyDetail.professionalDevelopment')) &&
    // Surveys that predate the configurable space types question keep the
    // legacy hardcoded check, otherwise the question itself covers it below.
    (hasQuestionWithDisplayComponent(surveyQuestions, SPACE_TYPES_FORM) ||
      !isEmpty(school.spaces)) &&
    requiredQuestions.every((question) =>
      hasRequiredQuestionResponse(question, school, disciplines)
    )
  )
}

function isCommunityPartnershipsComplete(data) {
  const { school, surveyQuestions, communitySurveyQuestions, disciplines } =
    data
  const requiredQuestions = getRequiredQuestionsForPage(
    surveyQuestions,
    Types.SURVEY_PAGES.COMMUNITY_AND_PARTNERSHIPS
  )

  return (
    (!get(communitySurveyQuestions, COMMUNITY_ENGAGEMENTS.NAME) ||
      !isEmpty(school.engagements)) &&
    requiredQuestions.every((question) =>
      hasRequiredQuestionResponse(question, school, disciplines)
    )
  )
}

function isBudgetPlanningComplete(data) {
  const { school, surveyQuestions, communitySurveyQuestions, disciplines } =
    data
  const requiredQuestions = getRequiredQuestionsForPage(
    surveyQuestions,
    Types.SURVEY_PAGES.BUDGET_AND_PLANNING
  )

  return (
    hasRequiredConfiguredResponse(
      school,
      communitySurveyQuestions,
      GOVERNANCE.NAME
    ) &&
    hasRequiredConfiguredResponse(
      school,
      communitySurveyQuestions,
      DISTRICT_SPENDING.NAME
    ) &&
    requiredQuestions.every((question) =>
      hasRequiredQuestionResponse(question, school, disciplines)
    )
  )
}

export function isSectionComplete(sectionKey, data) {
  const { school } = data
  if (!school) return false

  switch (sectionKey) {
    case SECTION_KEYS.STAFFING_INSTRUCTION:
      return isStaffingInstructionComplete(data)
    case SECTION_KEYS.COMMUNITY_PARTNERSHIPS:
      return isCommunityPartnershipsComplete(data)
    case SECTION_KEYS.BUDGET_PLANNING:
      return isBudgetPlanningComplete(data)
    case SECTION_KEYS.SUBMIT:
      return get(data, 'surveyAttempt.state') === SURVEY_ATTEMPT_STATES.COMPLETE
    default:
      return false
  }
}

export function deriveCompletedSections(data) {
  return getSections().filter((sectionKey) =>
    isSectionComplete(sectionKey, data)
  )
}

export function buildBarModel(data) {
  const sections = getSections()
  const completedSections = deriveCompletedSections(data)
  const completedSet = new Set(completedSections)
  const orderedCompletedSections = sections.filter((sectionKey) =>
    completedSet.has(sectionKey)
  )
  const orderedIncompleteSections = sections.filter(
    (sectionKey) => !completedSet.has(sectionKey)
  )

  return {
    sections: [...orderedCompletedSections, ...orderedIncompleteSections].map(
      (key) => ({ key, ...SECTION_META[key] })
    ),
    completedSections: orderedCompletedSections,
    percent: computePercent(completedSections.length, sections.length),
    totalCount: sections.length,
  }
}
